import {type ComponentPropsWithoutRef} from 'react';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';

import {ArrowLeftIcon} from '@/modules/foundation/components/icons/arrow-left-icon';
import {ArrowRightIcon} from '@/modules/foundation/components/icons/arrow-right-icon';
import {ImageBlock} from '@/modules/foundation/components/image/ImageBlock';
import {usePrefersReducedMotion} from '@/modules/foundation/hooks/usePrefersReducedMotion';
import {useDotButton} from '@/modules/page/components/content-blocks/gallery/hooks/useDotButton';
import {usePrevNextButtons} from '@/modules/page/components/content-blocks/gallery/hooks/usePrevNextButtons';

import {cn} from '../admin/lib/utils';

import type {UnionProductConfiguration} from './configuration';
import {getEnvironmentImages, getImageStackForConfiguration} from './configuration';
import {PRODUCT_IMAGE_SIDES} from './ProductImage';
import {ProductImageStack} from './ProductImageStack';
import type {UnionProductFragment} from './query';

type ProductImageSelectorProps = ComponentPropsWithoutRef<'div'> & {
	product: UnionProductFragment;
	config: UnionProductConfiguration;
};

export function ProductImageCarousel({
	product,
	config,
	className,
	...rest
}: ProductImageSelectorProps) {
	const productImagesStacks = PRODUCT_IMAGE_SIDES.map((side) =>
		getImageStackForConfiguration(side, product, config, false),
	).filter((e) => e !== null);

	const environmentImages = getEnvironmentImages(product, 1);

	const prefersReducedMotion = usePrefersReducedMotion();

	const totalSlides = productImagesStacks.length + environmentImages.length;

	const [emblaRef, emblaApi] = useEmblaCarousel({
		active: totalSlides > 1,
		duration: prefersReducedMotion ? 0 : 20,
	});
	const {selectedIndex, onDotButtonClick} = useDotButton(emblaApi);
	const {prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick} =
		usePrevNextButtons(emblaApi);

	return (
		<div className={cn('flex flex-col gap-2 sm:gap-4', className)} {...rest}>
			<div className="relative">
				<div ref={emblaRef} className="overflow-hidden rounded-md">
					<div className="flex touch-pan-y touch-pinch-zoom">
						{productImagesStacks.map((stack, i) => (
							<div
								className="bg-preview-background shrink-0 grow-0 basis-full p-4"
								key={i}
							>
								<ProductImageStack stack={stack} />
							</div>
						))}
						{environmentImages.map((image, i) => (
							<div
								className="bg-preview-background shrink-0 grow-0 basis-full"
								key={i}
							>
								<ImageBlock {...image} alt="" key={i} className="w-full" />
							</div>
						))}
					</div>

					{productImagesStacks.length + environmentImages.length > 1 && (
						<>
							<button
								type="button"
								onClick={onPrevButtonClick}
								disabled={prevBtnDisabled}
								aria-label="Forrige bilde"
								className={clsx(
									'absolute',
									'top-1/2',
									'left-2',
									'-translate-y-1/2',
									'p-2',
									'disabled:opacity-50',
									'bg-black/70',
									'rounded-sm',
									'cursor-pointer',
									'disabled:pointer-events-none',
								)}
							>
								<ArrowLeftIcon size={32} />
							</button>

							<button
								type="button"
								onClick={onNextButtonClick}
								disabled={nextBtnDisabled}
								aria-label="Neste bilde"
								className={clsx(
									'absolute',
									'top-1/2',
									'right-2',
									'-translate-y-1/2',
									'p-2',
									'disabled:opacity-50',
									'bg-black/70',
									'rounded-sm',
									'cursor-pointer',
									'disabled:pointer-events-none',
								)}
							>
								<ArrowRightIcon size={32} />
							</button>
						</>
					)}
				</div>
			</div>

			{productImagesStacks.length + environmentImages.length > 1 && (
				<div className="flex flex-row items-center justify-start gap-2 overflow-auto sm:gap-4 md:p-0">
					{productImagesStacks.map((stack, i) => (
						<button
							key={i}
							onClick={() => onDotButtonClick(i)}
							aria-label={
								'Knapp for bildevalg. Bildetext: ' + (stack.altText ?? 'ingen.') // FIXME: Should mention the product side, not the alt text.
							}
							className={clsx(
								'bg-preview-background aspect-square h-auto w-1/4 shrink-0 grow-0 overflow-hidden rounded-sm p-2 opacity-50 lg:w-1/5',
								{
									'opacity-100': selectedIndex === i,
								},
							)}
						>
							<ProductImageStack stack={stack} className="w-full" />
						</button>
					))}

					{environmentImages.map((image, i) => (
						<button
							key={i}
							onClick={() => onDotButtonClick(productImagesStacks.length + i)}
							aria-label={'Knapp for bildevalg.'}
							className={clsx(
								'bg-preview-background aspect-square h-auto w-1/4 shrink-0 grow-0 overflow-hidden rounded-sm opacity-50 lg:w-1/5',
								{
									'opacity-100': selectedIndex === productImagesStacks.length + i,
								},
							)}
						>
							<ImageBlock {...image} alt="" />
						</button>
					))}
				</div>
			)}
		</div>
	);
}
